import { type PropsWithChildren } from "react"
import { ChevronDownIcon, ChevronUpIcon, HelpCircleIcon } from "lucide-react"
import { z } from "zod"
import {
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Form,
  FormDescription,
  FormControl,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm, DefaultValues } from "react-hook-form"
import { Button } from "@/components/ui/button"
import { useState } from "react"
import { CaretSortIcon, CheckIcon } from "@radix-ui/react-icons"
import { cn, zNumberNull, zStringNull } from "@/lib/utils"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  CalculationMethod,
  CalculationParameters,
  Madhab,
  HighLatitudeRule,
  Rounding,
  PolarCircleResolution,
  Shafaq,
} from "adhan"
import { capitalCase } from "change-case"
import { ScrollArea } from "@/components/ui/scroll-area"
import { adjustmentInfo } from "@/lib/adhan"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Timetable } from "./timetable"
import { useToast } from "../ui/use-toast"
import { queryClient } from "@/lib/react-query"
import { client, validateClientProperty } from "@/lib/ts-rest"
import type { PrayerTimeConfiguration } from "@speaker/database"
import { trackEvent } from "@/lib/log"

const methods: {
  label: string
  value: string
}[] = Object.keys(CalculationMethod).map((item) => ({
  label: capitalCase(item),
  value: item,
}))
const madhabs: {
  label: string
  value: string
}[] = Object.entries(Madhab).map((item) => ({
  label: capitalCase(item[0]),
  value: item[1],
}))
const highLatitudeRules: {
  label: string
  value: string
}[] = Object.entries(HighLatitudeRule)
  .map((item) => ({
    label: capitalCase(item[0]),
    value: item[1],
  }))
  .filter((item): item is any => typeof item.value === "string")
const roundings: {
  label: string
  value: string
}[] = Object.entries(Rounding).map((item) => ({
  label: capitalCase(item[0]),
  value: item[1],
}))
const polarCircleResolutions: {
  label: string
  value: string
}[] = Object.entries(PolarCircleResolution).map((item) => ({
  label: capitalCase(item[0]),
  value: item[1],
}))
const shafaqs: {
  label: string
  value: string
}[] = Object.entries(Shafaq).map((item) => ({
  label: capitalCase(item[0]),
  value: item[1],
}))

const formSchema = z.object({
  method: zStringNull,
  madhab: zStringNull,
  fajrAngle: zNumberNull,
  maghribAngle: zNumberNull,
  ishaAngle: zNumberNull,
  ishaInterval: zNumberNull,
  highLatitudeRule: zStringNull,
  fajrAdjustment: zNumberNull,
  sunriseAdjustment: zNumberNull,
  dhuhrAdjustment: zNumberNull,
  asrAdjustment: zNumberNull,
  maghribAdjustment: zNumberNull,
  ishaAdjustment: zNumberNull,
  rounding: zStringNull,
  polarCircleResolution: zStringNull,
  shafaq: zStringNull,
})

export function UpdatePrayerTimeConfiguration({
  prayerTimeConfiguration,
}: {
  prayerTimeConfiguration: PrayerTimeConfiguration & {
    latitude: number
    longitude: number
  }
}) {
  const { mutate: updatePrayerTimeConfiguration } =
    client.updateIntegration.useMutation({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [validateClientProperty("getIntegrations")],
        })
        toast({ title: "Prayer times updated." })
      },
    })
  const { toast } = useToast()

  const defaultValues: DefaultValues<z.infer<typeof formSchema>> = {
    method: "MoonsightingCommittee",
    fajrAngle: null,
    maghribAngle: null,
    ishaAngle: null,
    ishaInterval: null,
    madhab: null,
    highLatitudeRule: null,
    fajrAdjustment: null,
    sunriseAdjustment: null,
    dhuhrAdjustment: null,
    asrAdjustment: null,
    maghribAdjustment: null,
    ishaAdjustment: null,
    rounding: null,
    polarCircleResolution: null,
    shafaq: null,
  }

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues,
    values: {
      method: prayerTimeConfiguration?.method ?? "MoonsightingCommittee",
      madhab: prayerTimeConfiguration?.madhab ?? null,
      fajrAngle: prayerTimeConfiguration?.fajrAngle
        ? Number(prayerTimeConfiguration.fajrAngle)
        : null,
      maghribAngle: prayerTimeConfiguration?.maghribAngle
        ? Number(prayerTimeConfiguration.maghribAngle)
        : null,
      ishaAngle: prayerTimeConfiguration?.ishaAngle
        ? Number(prayerTimeConfiguration.ishaAngle)
        : null,
      ishaInterval: prayerTimeConfiguration?.ishaInterval ?? null,
      highLatitudeRule: prayerTimeConfiguration?.highLatitudeRule ?? null,
      fajrAdjustment: prayerTimeConfiguration?.fajrAdjustment ?? null,
      sunriseAdjustment: prayerTimeConfiguration?.sunriseAdjustment ?? null,
      dhuhrAdjustment: prayerTimeConfiguration?.dhuhrAdjustment ?? null,
      asrAdjustment: prayerTimeConfiguration?.asrAdjustment ?? null,
      maghribAdjustment: prayerTimeConfiguration?.maghribAdjustment ?? null,
      ishaAdjustment: prayerTimeConfiguration?.ishaAdjustment ?? null,
      rounding: prayerTimeConfiguration?.rounding ?? null,
      polarCircleResolution:
        prayerTimeConfiguration?.polarCircleResolution ?? null,
      shafaq: prayerTimeConfiguration?.shafaq ?? null,
    },
  })

  const formValues = form.getValues()

  function resetForm() {
    trackEvent("reset prayer time settings")
    form.reset(defaultValues)
  }

  function submitForm(data: z.infer<typeof formSchema>): void {
    trackEvent("saved prayer time settings", { integrationId: prayerTimeConfiguration.integrationId, method: data.method })
    const { latitude, longitude, locationAddress, locationName } =
      prayerTimeConfiguration
    updatePrayerTimeConfiguration({
      body: {
        prayerTimeConfiguration: {
          latitude,
          locationAddress,
          locationName,
          longitude,
          ...data,
        },
        integration: {},
      },
      params: {
        integrationId: String(prayerTimeConfiguration.integrationId),
      },
    })
  }

  const params: CalculationParameters | undefined = (CalculationMethod as any)[
    formValues.method ?? ""
  ]?.()

  const [showSimple, setShowSimple] = useState(false)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [showAngles, setShowAngles] = useState(false)
  const [showAdjustments, setShowAdjustments] = useState(false)
  const [showOther, setShowOther] = useState(false)

  const [methodOpen, setMethodOpen] = useState(false)

  const tabs = ["Preview", "Simple Settings", "Advanced Settings"] as const
  const [tab, setTab] = useState<(typeof tabs)[number]>("Preview")

  return (
    <>
      <div className="rounded-xl bg-gray-50 p-2 flex gap-3 mb-5">
        {tabs.map((item) => (
          <div
            onClick={() => { trackEvent("switched prayer settings tab", { tab: item }); setTab(item) }}
            className={cn(
              "p-2 rounded-lg text-xs cursor-pointer hover:bg-primary hover:text-white transition",
              item === tab && "!bg-primary !text-white"
            )}
            key={item}
          >
            {item}
          </div>
        ))}
      </div>
      {tab === "Preview" && (
        <Timetable integrationId={prayerTimeConfiguration.integrationId} />
      )}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(submitForm)}>
          {tab === "Simple Settings" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 ">
              {/* method */}
              <FormField
                control={form.control}
                name="method"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>
                      {capitalCase(
                        adjustmentInfo.find((item) => item.title === "method")
                          ?.title ?? ""
                      )}
                    </FormLabel>
                    <div className="flex flex-col md:flex-row gap-3 overflow-auto">
                      <Popover open={methodOpen} onOpenChange={setMethodOpen}>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              role="combobox"
                              className={cn(
                                "justify-between w-full",
                                !field.value && "text-muted-foreground"
                              )}
                            >
                              {field.value
                                ? methods.find(
                                    (item) => item.value === field.value
                                  )?.label
                                : "Select method"}
                              <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent avoidCollisions={false} className="p-0">
                          <Command>
                            <CommandInput
                              placeholder="Search methods..."
                              className="h-9"
                            />
                            <CommandEmpty>No methods found.</CommandEmpty>
                            <CommandGroup>
                              {methods.map((item) => (
                                <CommandItem
                                  value={String(item.value)}
                                  key={item.value}
                                  onSelect={() => {
                                    form.setValue("method", item.value)
                                    setMethodOpen(false)
                                  }}
                                >
                                  {item.label}
                                  <CheckIcon
                                    className={cn(
                                      "ml-auto h-4 w-4",
                                      item.value === field.value
                                        ? "opacity-100"
                                        : "opacity-0"
                                    )}
                                  />
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      <AdjustmentInfoDialog title="method" />
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {/* madhab */}
              <FormField
                control={form.control}
                name="madhab"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>
                      {capitalCase(
                        adjustmentInfo.find((item) => item.title === "madhab")
                          ?.title ?? ""
                      )}
                    </FormLabel>
                    <div className="flex gap-3">
                      <Select
                        onValueChange={field.onChange}
                        value={field.value ?? ""}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select madhab" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {madhabs.map((item) => (
                            <SelectItem key={item.value} value={item.value}>
                              {item.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <AdjustmentInfoDialog title="madhab" />
                    </div>
                    <FormDescription>
                      If empty, defaults to "Shafi"
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          )}
          {tab === "Advanced Settings" && (
            <div className="flex flex-col gap-3">
              <Tab
                onClick={() => setShowAngles(!showAngles)}
                enabled={showAngles}
                title="Angles"
              >
                <div className="grid md:grid-cols-2 gap-5">
                  {/* fajrAngle */}
                  <FormField
                    control={form.control}
                    name="fajrAngle"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel> {capitalCase("fajrAngle")}</FormLabel>
                        <div className="flex gap-3">
                          <FormControl>
                            <Input {...field} value={field.value ?? ""} />
                          </FormControl>
                          <AdjustmentInfoDialog title="fajrAngle" />
                        </div>
                        <FormDescription>
                          If empty, defaults to {params?.fajrAngle}°
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {/* maghribAngle */}
                  <FormField
                    control={form.control}
                    name="maghribAngle"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel> {capitalCase("maghribAngle")}</FormLabel>
                        <div className="flex gap-3">
                          <FormControl>
                            <Input {...field} value={field.value ?? ""} />
                          </FormControl>
                          <AdjustmentInfoDialog title="maghribAngle" />
                        </div>
                        <FormDescription>
                          If empty, defaults to {params?.maghribAngle}°
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {/* ishaAngle */}
                  <FormField
                    control={form.control}
                    name="ishaAngle"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel>{capitalCase("ishaAngle")}</FormLabel>
                        <div className="flex gap-3">
                          <FormControl>
                            <Input {...field} value={field.value ?? ""} />
                          </FormControl>
                          <AdjustmentInfoDialog title="ishaAngle" />
                        </div>
                        <FormDescription>
                          If empty, defaults to {params?.ishaAngle}°
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {/* ishaInterval */}
                  <FormField
                    control={form.control}
                    name="ishaInterval"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel>{capitalCase("ishaInterval")}</FormLabel>
                        <div className="flex gap-3">
                          <FormControl>
                            <Input {...field} value={field.value ?? ""} />
                          </FormControl>
                          <AdjustmentInfoDialog title="ishaInterval" />
                        </div>
                        <FormDescription>
                          If empty, defaults to {params?.ishaInterval} minutes
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </Tab>
              <Tab
                onClick={() => setShowAdjustments(!showAdjustments)}
                enabled={showAdjustments}
                title="Adjustments"
              >
                <div className="grid md:grid-cols-2 gap-5">
                  {/* fajrAdjustment */}
                  <FormField
                    control={form.control}
                    name="fajrAdjustment"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel>{capitalCase("fajrAdjustment")}</FormLabel>
                        <div className="flex gap-3">
                          <FormControl>
                            <Input {...field} value={field.value ?? ""} />
                          </FormControl>
                          <AdjustmentInfoDialog title="fajrAdjustment" />
                        </div>
                        <FormDescription>
                          If empty, defaults to {params?.adjustments.fajr}{" "}
                          minutes
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {/* sunriseAdjustment */}
                  <FormField
                    control={form.control}
                    name="sunriseAdjustment"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel>
                          {capitalCase("sunriseAdjustment")}
                        </FormLabel>
                        <div className="flex gap-3">
                          <FormControl>
                            <Input {...field} value={field.value ?? ""} />
                          </FormControl>
                          <AdjustmentInfoDialog title="sunriseAdjustment" />
                        </div>
                        <FormDescription>
                          If empty, defaults to {params?.adjustments.sunrise}{" "}
                          minutes
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {/* dhuhrAdjustment */}
                  <FormField
                    control={form.control}
                    name="dhuhrAdjustment"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel>{capitalCase("dhuhrAdjustment")}</FormLabel>
                        <div className="flex gap-3">
                          <FormControl>
                            <Input {...field} value={field.value ?? ""} />
                          </FormControl>
                          <AdjustmentInfoDialog title="dhuhrAdjustment" />
                        </div>
                        <FormDescription>
                          If empty, defaults to {params?.adjustments.dhuhr}{" "}
                          minutes
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {/* asrAdjustment */}
                  <FormField
                    control={form.control}
                    name="asrAdjustment"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel>{capitalCase("asrAdjustment")}</FormLabel>
                        <div className="flex gap-3">
                          <FormControl>
                            <Input {...field} value={field.value ?? ""} />
                          </FormControl>
                          <AdjustmentInfoDialog title="asrAdjustment" />
                        </div>
                        <FormDescription>
                          If empty, defaults to {params?.adjustments.asr}{" "}
                          minutes
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {/* maghribAdjustment */}
                  <FormField
                    control={form.control}
                    name="maghribAdjustment"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel>
                          {capitalCase("maghribAdjustment")}
                        </FormLabel>
                        <div className="flex gap-3">
                          <FormControl>
                            <Input {...field} value={field.value ?? ""} />
                          </FormControl>
                          <AdjustmentInfoDialog title="maghribAdjustment" />
                        </div>
                        <FormDescription>
                          If empty, defaults to {params?.adjustments.maghrib}{" "}
                          minutes
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {/* ishaAdjustment */}
                  <FormField
                    control={form.control}
                    name="ishaAdjustment"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel>{capitalCase("ishaAdjustment")}</FormLabel>
                        <div className="flex gap-3">
                          <FormControl>
                            <Input {...field} value={field.value ?? ""} />
                          </FormControl>
                          <AdjustmentInfoDialog title="ishaAdjustment" />
                        </div>
                        <FormDescription>
                          If empty, defaults to {params?.adjustments.isha}{" "}
                          minutes
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </Tab>
              <Tab
                onClick={() => setShowOther(!showOther)}
                enabled={showOther}
                title="Other"
              >
                <div className="grid md:grid-cols-2 gap-5">
                  {/* highLatitudeRule */}
                  <FormField
                    control={form.control}
                    name="highLatitudeRule"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel>
                          {capitalCase(
                            adjustmentInfo.find(
                              (item) => item.title === "highLatitudeRule"
                            )?.title ?? ""
                          )}
                        </FormLabel>
                        <div className="flex gap-3">
                          <Select
                            onValueChange={field.onChange}
                            value={field.value ?? ""}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select high latitude rule" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {highLatitudeRules.map((item) => (
                                <SelectItem key={item.value} value={item.value}>
                                  {item.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <AdjustmentInfoDialog title="highLatitudeRule" />
                        </div>
                        <FormDescription>
                          If empty, defaults to "
                          {typeof params?.highLatitudeRule === "string" &&
                            capitalCase(
                              highLatitudeRules.find(
                                (item) => item.value === params.highLatitudeRule
                              )?.label ?? ""
                            )}
                          ""
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {/* rounding */}
                  <FormField
                    control={form.control}
                    name="rounding"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel>
                          {capitalCase(
                            adjustmentInfo.find(
                              (item) => item.title === "rounding"
                            )?.title ?? ""
                          )}
                        </FormLabel>
                        <div className="flex gap-3">
                          <Select
                            onValueChange={field.onChange}
                            value={field.value ?? ""}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select rounding" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {roundings.map((item) => (
                                <SelectItem key={item.value} value={item.value}>
                                  {item.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <AdjustmentInfoDialog title="rounding" />
                        </div>
                        <FormDescription>
                          If empty, defaults to "
                          {typeof params?.rounding === "string" &&
                            capitalCase(
                              roundings.find(
                                (item) => item.value === params.rounding
                              )?.label ?? ""
                            )}
                          ""
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {/* polarCircleResolution */}
                  <FormField
                    control={form.control}
                    name="polarCircleResolution"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel>
                          {capitalCase(
                            adjustmentInfo.find(
                              (item) => item.title === "polarCircleResolution"
                            )?.title ?? ""
                          )}
                        </FormLabel>
                        <div className="flex gap-3">
                          <Select
                            onValueChange={field.onChange}
                            value={field.value ?? ""}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select polar circle resolution" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {polarCircleResolutions.map((item) => (
                                <SelectItem key={item.value} value={item.value}>
                                  {item.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <AdjustmentInfoDialog title="polarCircleResolution" />
                        </div>
                        <FormDescription>
                          If empty, defaults to "
                          {typeof params?.polarCircleResolution === "string" &&
                            capitalCase(
                              polarCircleResolutions.find(
                                (item) =>
                                  item.value === params.polarCircleResolution
                              )?.label ?? ""
                            )}
                          ""
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {/* shafaq */}
                  <FormField
                    control={form.control}
                    name="shafaq"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel>
                          {capitalCase(
                            adjustmentInfo.find(
                              (item) => item.title === "shafaq"
                            )?.title ?? ""
                          )}
                        </FormLabel>
                        <div className="flex gap-3">
                          <Select
                            onValueChange={field.onChange}
                            value={field.value ?? ""}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select shafaq" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {shafaqs.map((item) => (
                                <SelectItem key={item.value} value={item.value}>
                                  {item.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <AdjustmentInfoDialog title="shafaq" />
                        </div>
                        <FormDescription>
                          If empty, defaults to "
                          {typeof params?.shafaq === "string" &&
                            capitalCase(
                              shafaqs.find(
                                (item) => item.value === params.shafaq
                              )?.label ?? ""
                            )}
                          ""
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </Tab>
            </div>
          )}
          {(tab === "Simple Settings" || tab === "Advanced Settings") && (
            <div className="mt-5 flex gap-3">
              <Button>Update</Button>
              <Button
                onClick={() => resetForm()}
                type="button"
                variant="outline"
              >
                Reset to defaults
              </Button>
            </div>
          )}
        </form>
      </Form>
    </>
  )
}

function AdjustmentInfoDialog({ title }: { title: string }) {
  return (
    <>
      {adjustmentInfo.find((item) => item.title === title) && (
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="outline">
              <HelpCircleIcon className="h-5 w-5" />
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-[95vw] md:max-w-[80vw]">
            <DialogHeader>
              <DialogTitle>
                {capitalCase(
                  adjustmentInfo.find((item) => item.title === title)?.title ??
                    ""
                )}
              </DialogTitle>
            </DialogHeader>
            <div className="overflow-auto">
              <div>
                {
                  adjustmentInfo.find((item) => item.title === title)
                    ?.description
                }
              </div>
              {adjustmentInfo.find((item) => item.title === title)?.options && (
                <ScrollArea className="h-[80vh] rounded-md border mt-3">
                  <div className="flex flex-col gap-3 p-4">
                    {adjustmentInfo
                      .find((item) => item.title === title)
                      ?.options?.map((item) => (
                        <div key={item.value}>
                          <div className="text-sm">
                            {capitalCase(item.value)}
                          </div>
                          <div className="text-sm text-muted-foreground font-light">
                            {item.description}
                          </div>
                        </div>
                      ))}
                  </div>
                </ScrollArea>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}

function Tab({
  enabled,
  title,
  children,
  onClick,
}: PropsWithChildren<{
  title: string
  enabled: boolean
  onClick: () => void
}>) {
  return (
    <div>
      <div
        onClick={() => onClick()}
        className="max-w-[250px] px-3 py-2 rounded-lg text-primary font-medium bg-primary-foreground flex justify-between items-center cursor-pointer hover:"
      >
        <div>{title}</div>
        {enabled ? (
          <ChevronDownIcon className="w-5 h-5" />
        ) : (
          <ChevronUpIcon className="w-5 h-5" />
        )}
      </div>
      {enabled && <div className="mt-3 pl-3">{children}</div>}
    </div>
  )
}
